<template>
  <div class="container-fluid creatives-wrapper" :class="{ 'conversion-test--modal': isModal }">
      <div class="col-md-12 conversion-test__col">
        <box class="creative-single-box">
          <div class="form-group inner full-width">
            <div class="conversion-test__description mb-2">
              <p>
                You are currently previewing the conversion testing functionality of SmartyAds. This page confirms that conversion tracking within the SmartyAds tracking system is configured and operating correctly.
              </p>
              <p>
                The next step in the conversion flow should redirect you to your intended final landing page. To complete the test, please click the link below. If the redirection does not lead to the expected destination, kindly review and adjust your conversion tracking configuration.
              </p>
            </div>
          </div>

          <div class="conversion-test__link-row">
            <a :href="isTestLinkReady ? testLinkUrl : null"
               target="_blank"
               rel="noopener"
               class="conversion-test__link"
               :class="{ 'conversion-test__link--disabled': !isTestLinkReady }"
               :aria-disabled="!isTestLinkReady"
               @click="onTestLinkClick">
              {{ testLinkUrl }}
            </a>
          </div>

          <div class="conversion-test__table">
            <div class="conversion-test__cell conversion-test__cell--label">Status</div>
            <div class="conversion-test__cell">{{ statusText }}</div>

            <div class="conversion-test__cell conversion-test__cell--label">Partner ID</div>
            <div class="conversion-test__cell">{{ partnerId || '-' }}</div>

            <div class="conversion-test__cell conversion-test__cell--label">Advertiser ID</div>
            <div class="conversion-test__cell">{{ advertiserId || '-' }}</div>

            <div class="conversion-test__cell conversion-test__cell--label">Creative ID</div>
            <div class="conversion-test__cell">{{ creativeId || crid || '-' }}</div>
          </div>

          <div class="btn-wrap text-right">
            <dspButton v-if="!isModal" class="plain" @click="$router.back()">Back</dspButton>
            <dspButton v-else class="plain" @click="$emit('close')">Close</dspButton>
            <dspButton class="primary" :isDisabled="!isTestLinkReady" @click="open()">Open Test Link</dspButton>
          </div>
        </box>
      </div>
  </div>
</template>

<script>
 function stripWrappingBraces (value) {
   if (value == null) return value;
   const s = String(value);
   return /^\{[^{}]*\}$/.test(s) ? s.slice(1, -1) : s;
 }

export default {
  name: 'conversion-test',
  props: {
    pClickUrl: {
      type: String,
      default: '',
    },
    pCampaignId: {
      type: [String, Number],
      default: null,
    },
    pCreativeId: {
      type: [String, Number],
      default: null,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      clickUrl: this.pClickUrl || (this.$route && this.$route.query ? this.$route.query.click_url : '') || '',
      cid: stripWrappingBraces(this.pCampaignId || (this.$route && this.$route.query ? (this.$route.query.campaign_id || this.$route.query.cid) : null) || null),
      crid: stripWrappingBraces(this.pCreativeId || (this.$route && this.$route.query ? (this.$route.query.creative_id || this.$route.query.crid) : null) || null),
      token: (this.$route && this.$route.query ? this.$route.query.token : '') || '',
      publicCode: (this.$route && this.$route.params ? this.$route.params.code : '') || (this.$route && this.$route.query ? this.$route.query.code : '') || '',
      userId: (this.$auth && typeof this.$auth.user === 'function') ? (this.$auth.user().id) : null,
      macros: {},
      finalAfImpressionUrl: '',
      finalAfClickUrl: '',
      analyticsTagTemplate: '',
      analyticsTagIsMissing: false,
      isAfImpressionSkipped: false,
      statusText: 'Preparing... ',
      partnerId: '',
      advertiserId: '',
      creativeId: '',
      isFlowCompleted: false,
    };
  },
  computed: {
    testLinkUrl () {
      return this.finalAfClickUrl || this.clickUrl;
    },

    isTestLinkReady () {
      return this.isFlowCompleted && Boolean(this.testLinkUrl);
    },
  },
  mounted () {
    this.runFlow();
  },
  methods: {
    logExplorerUrl (event, url) {
      if (!url || !this.token) return;

      const payload = {
        token: this.token,
        event: event || 'open',
        url,
        userId: this.userId,
        cid: this.cid,
        crid: this.crid,
      };

      this.axios.post('/creatives/conversion-test/log', payload).catch(() => {
      });
    },

    async runFlow () {
      this.isFlowCompleted = false;
      this.analyticsTagIsMissing = false;
      this.isAfImpressionSkipped = false;

      try {
        if (this.publicCode && !this.token) {
          this.statusText = 'Resolving test link...';
          await this.resolvePublicCode();
        }

        if (!this.token) {
          throw new Error('invalid conversion test link');
        }

        this.statusText = '1/4 test-impression...';
        const testImp = await this.callTestImpression();
        this.macros = testImp.macros || {};
        if (this.cid != null && (this.macros.CAMPAIGN_ID == null || this.macros.CAMPAIGN_ID === '')) {
          this.macros.CAMPAIGN_ID = String(this.cid);
        }
        if (this.crid != null && (this.macros.CREATIVE_ID == null || this.macros.CREATIVE_ID === '')) {
          this.macros.CREATIVE_ID = String(this.crid);
        }
        this.partnerId = (testImp.impressionData && String(testImp.impressionData.publisherId || '')) || '';
        this.advertiserId = (testImp.impressionData && String(testImp.impressionData.advertiserId || '')) || '';
        this.creativeId = (testImp.impressionData && String(testImp.impressionData.creativeId || '')) || '';

        this.statusText = '2/4 AF impression...';
        const afImpUrl = await this.buildAfImpressionUrl();
        this.finalAfImpressionUrl = afImpUrl;
        if (!afImpUrl) {
          if (!this.analyticsTagIsMissing) {
            throw new Error('AF impression failed');
          }

          this.isAfImpressionSkipped = true;
          this.statusText = '2/4 AF impression skipped: campaign analytics tag is not configured.';
        } else {
          this.logExplorerUrl('af_impression_fire', afImpUrl);
          if (!await this.firePixel(afImpUrl)) {
            throw new Error('AF impression failed');
          }
        }

        this.statusText = '3/4 click...';
        if (!await this.callClick()) {
          throw new Error('click failed');
        }

        this.statusText = '4/4 AF click...';
        const afClick = this.buildAfClickUrl();
        this.finalAfClickUrl = afClick;
        if (!afClick) {
          throw new Error('AF click failed');
        }
        this.logExplorerUrl('af_click_fire', afClick);
        if (!await this.firePixel(afClick)) {
          throw new Error('AF click failed');
        }

        this.isFlowCompleted = true;
        this.statusText = this.isAfImpressionSkipped
          ? 'The Creative has successfully been tracked. AF impression was skipped because the campaign analytics tag is not configured.'
          : 'The Creative has successfully been tracked.';
      } catch (e) {
        this.statusText = 'Error: ' + (e && e.message ? e.message : 'unexpected error');
      }
    },

    async resolvePublicCode () {
      const resp = await this.axios.get(`/creatives/conversion-test/public-link/${this.publicCode}`);
      const data = resp && resp.data && resp.data.data ? resp.data.data : {};

      this.clickUrl = data.click_url || '';
      this.cid = stripWrappingBraces(data.campaign_id || data.cid || null);
      this.crid = stripWrappingBraces(data.creative_id || data.crid || null);
      this.token = data.token || '';

      if (!this.clickUrl || !this.cid || !this.crid || !this.token) {
        throw new Error('invalid conversion test link');
      }
    },

    async callTestImpression () {
      try {
        const model = await this.getDeviceModel();
        const payload = {
          token: this.token,
          cid: this.cid,
          crid: this.crid,
          click_url: this.clickUrl,
          host: 'dsp.smartyads.com',
          ua: navigator.userAgent,
          os: this.getOsName(),
          osv: this.getOsVersion(),
          language: navigator.language,
          browser: this.getBrowserName(),
          deviceType: this.getDeviceType(),
          model,
        };
        const resp = await this.axios.post('/creatives/conversion-test/test-impression', payload);
        return resp && resp.data ? resp.data : {};
      } catch (e) {
        throw new Error('test-impression failed');
      }
    },

    async getDeviceModel () {
      const userAgentData = navigator.userAgentData;
      if (userAgentData && Array.isArray(userAgentData.brands) && typeof userAgentData.getHighEntropyValues === 'function') {
        try {
          const entropyValues = await userAgentData.getHighEntropyValues(['model']);
          if (entropyValues && entropyValues.model) {
            return entropyValues.model.trim();
          }
        } catch (e) {
          void e;
        }
      }

      const match = navigator.userAgent.match(/Android[^;]*;\s*([^;)]+)/i);
      if (match && match[1]) {
        return match[1].trim();
      }

      if (/iPhone/i.test(navigator.userAgent)) {
        return 'iPhone';
      }

      if (/iPad/i.test(navigator.userAgent)) {
        return 'iPad';
      }

      if (/Android/i.test(navigator.userAgent)) {
        return 'Android Device';
      }

      if (/Windows/i.test(navigator.userAgent)) {
        return 'Windows PC';
      }

      if (/Macintosh|Mac OS X/i.test(navigator.userAgent)) {
        return 'Mac';
      }

      if (/Linux/i.test(navigator.userAgent)) {
        return 'Linux Device';
      }

      return '';
    },

    getOsName () {
      const ua = navigator.userAgent;

      if (/Android/i.test(ua)) {
        return 'android';
      }

      if (/iPhone|iPad|iPod/i.test(ua)) {
        return 'ios';
      }

      if (/Windows/i.test(ua)) {
        return 'windows';
      }

      if (/Mac OS X|Macintosh/i.test(ua)) {
        return 'macos';
      }

      if (/Linux/i.test(ua)) {
        return 'linux';
      }

      return '';
    },

    getOsVersion () {
      const ua = navigator.userAgent;

      let match = ua.match(/Android\s+([\d.]+)/i);
      if (match && match[1]) {
        return match[1];
      }

      match = ua.match(/OS\s([\d_]+)/i);
      if (match && match[1]) {
        return match[1].replace(/_/g, '.');
      }

      match = ua.match(/Windows NT\s([\d.]+)/i);
      if (match && match[1]) {
        return match[1];
      }

      match = ua.match(/Mac OS X\s([\d_]+)/i);
      if (match && match[1]) {
        return match[1].replace(/_/g, '.');
      }

      return '';
    },

    getBrowserName () {
      const ua = navigator.userAgent;

      if (/Edg\//i.test(ua)) {
        return 'edge';
      }

      if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) {
        return 'chrome';
      }

      if (/Firefox\//i.test(ua)) {
        return 'firefox';
      }

      if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) {
        return 'safari';
      }

      return '';
    },

    getDeviceType () {
      const ua = navigator.userAgent;

      if (/SMART-TV|SmartTV|GoogleTV|HbbTV|CrKey|Roku|AppleTV|Tizen|Web0S|WebOS/i.test(ua)) {
        return 3;
      }

      if (/iPad|Tablet/i.test(ua)) {
        return 5;
      }

      if (/iPhone|Android/i.test(ua) && /Mobile/i.test(ua)) {
        return 4;
      }

      if (/Macintosh|Windows|Linux/i.test(ua)) {
        return 2;
      }

      return 0;
    },

    getCountryFromBrowser () {
      const language = navigator.language || '';

      if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function' && language) {
        try {
          const locale = new Intl.Locale(language);
          if (locale.region) {
            return locale.region;
          }
        } catch (e) {
          void e;
        }
      }

      const match = language.match(/[-_]([A-Za-z]{2})$/);
      if (match && match[1]) {
        return match[1].toUpperCase();
      }

      return '';
    },

    async buildAfImpressionUrl () {
      if (!this.cid) return '';
      try {
        const resp = await this.axios.get(`/creatives/conversion-test/campaign/${this.cid}`, {
          params: { token: this.token }
        });
        const tag = resp && resp.data && resp.data.data ? (resp.data.data.analyticsTag || '') : '';
        this.analyticsTagTemplate = tag;
        if (!tag || !tag.trim()) {
          this.analyticsTagIsMissing = true;
          return '';
        }
        const url = this.applyMacros(tag, this.macros);
        return url;
      } catch (e) {
        return '';
      }
    },

    async callClick () {
      const hash = this.macros && this.macros.HASH;
      if (!hash) {
        return false;
      }
      try {
        await this.axios.get('/creatives/conversion-test/click', {
          params: {
            hash,
            token: this.token,
          },
        });
        return true;
      } catch (e) {
        return false;
      }
    },

    buildAfClickUrl () {
      if (!this.clickUrl) {
        return '';
      }
      let url = this.applyMacros(this.clickUrl, this.macros);
      // ensure clid param
      try {
        const u = new URL(url);
        if (!u.searchParams.get('clid') && this.macros && this.macros.CLICK_ID) {
          u.searchParams.set('clid', stripWrappingBraces(this.macros.CLICK_ID));
        }
        const out = u.toString();
        return out;
      } catch (e) {
        return url;
      }
    },

    applyMacros (template, macros) {
      if (!template || !macros) return template;
      let out = template;
      Object.keys(macros).forEach(k => {
        const v = stripWrappingBraces(String(macros[k] == null ? '' : macros[k]));
        const patterns = [
          new RegExp(`\\{${k}\\}`, 'g'),
          new RegExp(`\\[${k}\\]`, 'g'),
          new RegExp(`__${k}__`, 'g'),
          new RegExp(`%%${k}%%`, 'g'),
          new RegExp(`%${k}%`, 'g'),
        ];
        patterns.forEach(r => {
          out = out.replace(r, v);
        });
      });
      return out;
    },

    async firePixel (url) {
      try {
        await fetch(url, { method: 'GET', mode: 'no-cors' });
        return true;
      } catch (e) {
        return false;
      }
    },

    open () {
      if (!this.isTestLinkReady) return;
      const url = this.testLinkUrl;
      window.open(url, '_blank');
    },

    onTestLinkClick (event) {
      if (!this.isTestLinkReady) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.creatives-wrapper .main-title {
  margin-bottom: 16px;
}

.conversion-test__description p {
  margin: 0 0 14px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.conversion-test__description p:last-child {
  margin-bottom: 0;
}

.conversion-test__link-row {
  margin-top: 6px;
  margin-bottom: 10px;
}

.conversion-test__link {
  display: block;
  color: #2B6CB0;
  text-decoration: underline;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.conversion-test__link--disabled {
  color: #718096;
  cursor: not-allowed;
  pointer-events: none;
  text-decoration: none;
}

.conversion-test__table {
  display: grid;
  grid-template-columns: 140px 1fr;
  column-gap: 24px;
  row-gap: 10px;
  padding-top: 4px;
}

.conversion-test__cell--label {
  font-weight: 600;
}

.conversion-test--modal {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.conversion-test--modal .conversion-test__col {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.conversion-test--modal .creative-single-box {
  margin: 0 !important;
  padding: 12px !important;
}

.conversion-test--modal .form-group.inner {
  margin-bottom: 10px !important;
}

.conversion-test--modal .btn-wrap {
  margin-top: 8px !important;
}
</style>
